import type { CreatedProduct } from '@/types/products'

/**
 * Which product(s) a form is about (z8nrz7fxub): what the form bar shows beside
 * "P", what the picker offers, and when "P" turns red to ask.
 *
 * Pure so it is tested; see `workingProducts.test.ts`. The input is the
 * procedure's MAIN products (`procedureMainProducts`), so split aliquots and
 * re-coded products follow the rules the procedure label already uses.
 */

type Locatable = Pick<CreatedProduct, 'id' | 'storageNodeId' | 'locationPartnerId'> &
  Partial<Pick<CreatedProduct, 'volumeMl'>>

/**
 * A product still in hand: neither put into storage nor shipped to a partner,
 * nor used up by a split (0 mL, z8nrz7g2wm) — which also leaves no location.
 */
export function isAvailableProduct(
  p: Pick<CreatedProduct, 'storageNodeId' | 'locationPartnerId'> & Partial<Pick<CreatedProduct, 'volumeMl'>>,
): boolean {
  return !p.storageNodeId && !p.locationPartnerId && p.volumeMl !== 0
}

export interface WorkingProductState<T> {
  /** What the picker offers: the products still in hand. */
  offered: T[]
  /** What the bar names beside "P". Empty: nothing is shown. */
  selected: T[]
  /** "P" turns red and the picker asks: several in hand and none chosen. */
  needsChoice: boolean
}

/**
 * @param main      the procedure's main products
 * @param savedIds  the choice stored on the form (`FormSubmission.workingProductIds`)
 * @param editable  whether the form can still change (a closed form keeps its choice)
 */
export function workingProductState<T extends Locatable>(
  main: readonly T[],
  savedIds: readonly string[] | null | undefined,
  editable: boolean,
): WorkingProductState<T> {
  const offered = main.filter(isAvailableProduct)
  const saved = new Set(savedIds ?? [])

  if (!editable) {
    // A closed form shows what it was completed with, even if those bags have
    // since been stored or shipped; it never asks.
    const chosen = main.filter(p => saved.has(p.id))
    return { offered: [], selected: chosen.length ? chosen : offered.length === 1 ? offered : [], needsChoice: false }
  }
  if (offered.length <= 1) return { offered, selected: offered, needsChoice: false }

  // A stored or shipped bag drops out of the choice; if nothing chosen is left
  // in hand, the form asks again.
  const selected = offered.filter(p => saved.has(p.id))
  return { offered, selected, needsChoice: selected.length === 0 }
}
